/**
 * This is a wrapper library around postRobot to be used as a "client" for our shared
 * localStorage solution.
 */
import postRobot from 'post-robot';

// Import type definitions
/** @typedef {import('./types').RequestOptions} RequestOptions */
/** @typedef {import('./types').RequestMethod} RequestMethod */
/** @typedef {import('./types').OnStorageUpdateHandler} OnStorageUpdateHandler */
/** @typedef {import('./types').setupPostRobotListener} setupPostRobotListener */

let iframeReference;

// Function to solve the SI-6021 error by canceling the listener before creating it
function cancelListener(onStorageUpdate, url) {
  const listener = window.postRobot.on(
    'storageUpdate',
    {
      window: iframeReference.contentWindow,
      domain: url.origin,
    },
    onStorageUpdate,
  );

  listener.cancel();
}

/**
 * Create a postRobot listener for storage updates
 * @param {String} sharedStorageUrl the url to the sharedStorage
 * @param {OnStorageUpdateHandler} onStorageUpdate function to run when a storage update is detected
 */
function setupPostRobotListener(sharedStorageUrl, onStorageUpdate) {
  // Create an url object to easily get the origin
  const url = new URL(sharedStorageUrl);

  // Cancel listener
  cancelListener(onStorageUpdate, url);

  window.postRobot.on(
    'storageUpdate',
    {
      window: iframeReference.contentWindow,
      domain: url.origin,
    },
    onStorageUpdate,
  );
}

/**
 * Initialize the iframe for using the shared storage
 * @param {String} sharedStorageUrl the url where the shared storage recides
 * @param {OnStorageUpdateHandler?} onStorageUpdate function to run when a storage update is
 *        detected
 */
async function init(sharedStorageUrl, onStorageUpdate) {
  // Set the global postRobot instance
  window.postRobot = postRobot;

  // Check if the Iframe already exists
  const element = document.getElementById('ve-shared-storage');

  if (element !== null) {
    // Check if we have an iframe
    if (element.tagName === 'IFRAME') {
      // We have an iframe, check it's source
      const foundIframe = element;

      if (foundIframe.src === sharedStorageUrl) {
        // Use the found iframe as the reference
        iframeReference = foundIframe;
        return Promise.resolve('An sharedStorage iframe was found, using it as target!');
      }
    }

    // If it wasn't an iframe with the right source, reject with error
    return Promise.reject(new Error(
      'An element with the same id as the one used by sharedStorage has been found. Please make that the "ve-shared-storage" id is unused',
    ));
  }

  // Create the iframe
  iframeReference = document.createElement('iframe');
  iframeReference.id = 've-shared-storage';

  // Set the source
  iframeReference.src = sharedStorageUrl;

  // Hide it
  iframeReference.hidden = true;
  iframeReference.style.display = 'none';
  iframeReference.style.position = 'absolute';
  iframeReference.style.left = '-1000px';
  iframeReference.tabIndex = -1;

  // Create the promise to return
  const returnPromise = new Promise((resolve, reject) => {
    // Add timeout to reject the promise if the iframe didn't load
    const timeout = setTimeout(() => {
      console.warn('iframe timeout!');
      reject(new Error('Iframe loading timeout, 15sec passed without detecting the load event.'));
    }, 15000);

    // Add eventListener to detect when the iframe is ready
    iframeReference.addEventListener('load', () => {
      // Remove the timeout
      clearTimeout(timeout);
      // Setup update listener if a callback was provided
      if (onStorageUpdate) {
        setupPostRobotListener(sharedStorageUrl, onStorageUpdate);
      }
      resolve('Iframe ready!');
    });
  });

  // Append it to the page
  document.body.appendChild(iframeReference);

  // Return the promise
  return returnPromise;
}

/**
 * Request data from the sharedStorage
 * @param {RequestMethod} method which method to perform, similar to HTTP methods
 * @param {RequestOptions} options the options to send in the request. Key is always required,
 *        whereas value is only used for the "post" method
 * @returns Promise<string>
 */
async function request(method, options) {
  // Check if the iframeReference has been initialized
  if (!iframeReference) throw new Error('The sharedStorage iframe hasn\'t been initialized!');

  try {
    // Send a get message using postRobot
    const response = await postRobot.send(
      iframeReference.contentWindow,
      'request',
      {
        method,
        payload: options,
      },
    );
    return Promise.resolve(response.data.result);
  } catch (error) {
    return Promise.reject(error);
  }
}

export default {
  init,
  request,
};
